import React from 'react';
import styled from 'styled-components';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';

export type SectionHeadingSize = 'sm' | 'md';

export interface SectionHeadingProps {
  icon?: IconName;
  children: React.ReactNode;
  size?: SectionHeadingSize;
  className?: string;
  style?: React.CSSProperties;
  /** Optional trailing content, e.g. a count badge, right-aligned. */
  action?: React.ReactNode;
}

const Chip = styled.span<{ $size: SectionHeadingSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => ($size === 'sm' ? '24px' : '30px')};
  height: ${({ $size }) => ($size === 'sm' ? '24px' : '30px')};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary50};
  color: ${({ theme }) => theme.colors.primary700 || theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: ${({ $size }) => ($size === 'sm' ? '13px' : '16px')};
    height: ${({ $size }) => ($size === 'sm' ? '13px' : '16px')};
  }
`;

const Heading = styled.h3<{ $size: SectionHeadingSize }>`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  font-size: ${({ theme, $size }) => ($size === 'sm' ? theme.typography.fontSize.md : theme.typography.fontSize.lg)};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/**
 * Standard section header used across Tvikla OS pages: an icon in a tinted
 * rounded chip, followed by a bold title. Use instead of ad-hoc emoji-in-text
 * headers so every page (Shipments, Payments, Customs, Scheduling, ...)
 * shares the exact same visual treatment.
 */
export const SectionHeading: React.FC<SectionHeadingProps> = ({ icon, children, size = 'md', className, style, action }) => {
  return (
    <Row className={className} style={style}>
      <Heading $size={size}>
        {icon && (
          <Chip $size={size}>
            <Icon name={icon} size={size === 'sm' ? 13 : 16} />
          </Chip>
        )}
        {children}
      </Heading>
      {action}
    </Row>
  );
};
